import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Phone, Mail, Loader2, Sparkles, RefreshCw } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-purple-500/20 text-purple-600",
  contacted: "bg-blue-500/20 text-blue-500",
  converted: "bg-green-500/20 text-green-500",
  closed: "bg-gray-500/20 text-gray-500"
};

export const ChatLeadsSection = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [convo, setConvo] = useState(null);
  const [convoLoading, setConvoLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await api.get(`/concierge/leads${params}`);
      setLeads(res.data);
    } catch (e) {
      toast.error("Failed to load chat leads");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (lead, status) => {
    try {
      await api.put(`/concierge/leads/${lead.id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`Lead marked ${status}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const openConversation = async (lead) => {
    setConvoLoading(true);
    setConvo({ lead, messages: [] });
    try {
      const res = await api.get(`/concierge/leads/${lead.id}/conversation`);
      setConvo(res.data);
    } catch (e) {
      toast.error("Failed to load conversation");
      setConvo(null);
    } finally {
      setConvoLoading(false);
    }
  };

  return (
    <Card className="dashboard-card" data-testid="chat-leads-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Chat Leads
          {leads.filter((l) => l.status === "new").length > 0 && (
            <Badge className="bg-purple-500/20 text-purple-600">{leads.filter((l) => l.status === "new").length} new</Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchLeads}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No chat leads yet</p>
            <p className="text-sm">When website visitors share contact details with Lexi (the AI concierge), they'll appear here as leads.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map((lead) => (
              <div key={lead.id} className="p-4 rounded-lg bg-secondary/30 border border-white/5" data-testid={`chat-lead-${lead.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{lead.name || "Unknown visitor"}</p>
                      {lead.occasion && <Badge variant="outline" className="text-xs">{lead.occasion}</Badge>}
                      <Badge className={STATUS_COLORS[lead.status] || STATUS_COLORS.closed}>{lead.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{lead.trip_summary || "No summary yet"}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                      {lead.phone && (
                        <a href={`https://wa.me/${lead.phone.replace(/[^\d]/g, "").replace(/^0/, "61")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-green-600 hover:underline" data-testid={`lead-whatsapp-${lead.id}`}>
                          <Phone className="h-3 w-3" /> {lead.phone}
                        </a>
                      )}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-primary hover:underline">
                          <Mail className="h-3 w-3" /> {lead.email}
                        </a>
                      )}
                      <span>{lead.message_count || 0} messages</span>
                      {lead.last_message_at && <span>{new Date(lead.last_message_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openConversation(lead)} data-testid={`view-chat-${lead.id}`}>
                      <MessageSquare className="h-4 w-4 mr-1" /> View Chat
                    </Button>
                    <Select value={lead.status} onValueChange={(v) => updateStatus(lead, v)}>
                      <SelectTrigger className="w-32" data-testid={`lead-status-select-${lead.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!convo} onOpenChange={(open) => !open && setConvo(null)}>
        <DialogContent className="sm:max-w-[540px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Chat with {convo?.lead?.name || "visitor"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="lead-conversation">
            {convoLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : (
              (convo?.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`rounded-xl p-3 text-sm max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/50 border border-white/5"}`}>
                    {m.content}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ChatLeadsSection;
